import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { getUserImages } from '../Images/utilities/imageUtils.js';
import UploadImage from '../Images/UploadImage.jsx';
import IconDisplay from './IconDisplay.jsx';
import { ICON_OPTIONS, normalizeSkillIcon } from './iconOptions.js';

const IconPicker = ({ userId, value, onChange, disabled = false }) => {
  const [tab, setTab] = useState('icons');
  const [query, setQuery] = useState('');
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const selected = normalizeSkillIcon(value);

  const filtered = useMemo(() => ICON_OPTIONS.filter(({ label, value: optionValue }) =>
    `${label} ${optionValue}`.toLowerCase().includes(query.trim().toLowerCase())), [query]);

  const loadImages = useCallback(async () => {
    if (!userId) return;
    setLoadingImages(true);
    try { setImages(await getUserImages(userId) || []); } finally { setLoadingImages(false); }
  }, [userId]);

  useEffect(() => { if (tab === 'images') void loadImages(); }, [tab, loadImages]);

  return (
    <fieldset disabled={disabled} className="border rounded p-2">
      <legend className="float-none w-auto px-1 fs-6 mb-1">Skill icon</legend>
      <div className="d-flex align-items-center gap-2 mb-2">
        <IconDisplay icon={selected} userId={userId} label="Selected skill" size={44} />
        <small className="text-muted flex-grow-1">{selected ? 'Selected icon' : 'Default icon'}</small>
        {selected ? <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onChange(null)}>Remove</button> : null}
      </div>
      <div className="nav nav-tabs mb-2" role="tablist">
        <button type="button" className={`nav-link ${tab === 'icons' ? 'active' : ''}`} onClick={() => setTab('icons')}>Icon menu</button>
        <button type="button" className={`nav-link ${tab === 'images' ? 'active' : ''}`} onClick={() => setTab('images')}>My images</button>
      </div>
      {tab === 'icons' ? <>
        <label className="visually-hidden" htmlFor="skill-icon-search">Search icons</label>
        <input id="skill-icon-search" className="form-control form-control-sm mb-2" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search icons" />
        <div className="d-grid gap-1" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(56px, 1fr))', maxHeight: 190, overflowY: 'auto' }}>
          {filtered.map((option) => <button key={option.value} type="button" title={option.label} aria-label={`Select ${option.label} icon`} aria-pressed={selected?.type === 'bootstrap' && selected.value === option.value} className={`btn btn-sm ${selected?.value === option.value ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => onChange({ type: 'bootstrap', value: option.value })}><i className={`bi ${option.value}`} aria-hidden="true" /></button>)}
        </div>
      </> : <>
        <div className="row row-cols-3 g-2 mb-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
          {loadingImages ? <div className="col-12 small text-muted">Loading images...</div> : images.map((image) => <div className="col" key={image.id}><button type="button" className={`btn p-1 w-100 ${selected?.imageId === image.id ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => onChange({ type: 'image', imageId: image.id })} aria-label={`Use ${image.filename} as skill icon`} aria-pressed={selected?.imageId === image.id}><img src={image.base64} alt="" className="w-100 rounded" style={{ aspectRatio: '1', objectFit: 'cover' }} /></button></div>)}
          {!loadingImages && images.length === 0 ? <div className="col-12 small text-muted">No images uploaded yet.</div> : null}
        </div>
        <UploadImage userId={userId} onUploadSuccess={loadImages} />
      </>}
    </fieldset>
  );
};

IconPicker.propTypes = { userId: PropTypes.string.isRequired, value: PropTypes.object, onChange: PropTypes.func.isRequired, disabled: PropTypes.bool };
export default IconPicker;
